import { useState } from "react";
import { Box, Typography, Paper, Stack, TextField, MenuItem, Button, Alert, Tooltip, Grid2 as Grid } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxSexBand } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { LabTestPicker } from "@/components/LabTestPicker";
import { DxMasterPicker } from "@/components/DxMasterPicker";
import { DiagnosticOrderIdField } from "@/components/DiagnosticOrderIdField";
import { StatusPill } from "@/components/StatusPill";

/**
 * Samples drawn on one order, as a dropdown. The sample id is a Guid no screen can sensibly
 * display, so every bench action used to mean pasting one by hand — this picks it off the order.
 */
function SamplePicker({
  orderId, value, onChange, label = "Sample", helperText,
}: {
  orderId: string | null;
  value: string;
  onChange: (id: string) => void;
  label?: string;
  helperText?: string;
}) {
  const samples = useQuery({
    queryKey: ["dx-samples", orderId],
    queryFn: () => diagnosticsApi.listSamples(orderId!),
    enabled: !!orderId,
  });

  const rows = samples.data ?? [];
  return (
    <TextField
      select size="small" label={label} value={rows.some(s => s.id === value) ? value : ""}
      onChange={e => onChange(e.target.value)} disabled={!orderId}
      helperText={!orderId ? "Enter a diagnostic order first" : rows.length === 0 && !samples.isLoading ? "No samples collected for this order yet" : helperText}
    >
      <MenuItem value="">— select —</MenuItem>
      {rows.map(s => (
        <MenuItem key={s.id} value={s.id}>
          <Stack direction="row" spacing={1} alignItems="center">
            <span>{s.accessionNumber}</span>
            <StatusPill label={s.status} />
          </Stack>
        </MenuItem>
      ))}
    </TextField>
  );
}

export function LabWorkbenchPage() {
  const qc = useQueryClient();
  const [order, setOrder] = useState(""); const [specimen, setSpecimen] = useState(""); const [container, setContainer] = useState("");
  const collect = useMutation({
    meta: { successMessage: "Sample collected" },
    mutationFn: () => diagnosticsApi.collectSample({ diagnosticOrderId: order, specimenId: specimen, containerId: container }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dx-samples"] }),
  });

  const rejectionReasons = useQuery({ queryKey: ["dx-rejection-reasons"], queryFn: () => diagnosticsApi.listRejectionReasons() });

  // Bench panel — the order resolves to an id, and the sample is picked from that order's draws.
  const [benchOrder, setBenchOrder] = useState(""); const [benchOrderId, setBenchOrderId] = useState<string | null>(null);
  const [sampleId, setSampleId] = useState(""); const [rejectReasonId, setRejectReasonId] = useState(""); const [rejectReason, setRejectReason] = useState(""); const [photoUrl, setPhotoUrl] = useState("");
  const afterSampleChange = () => qc.invalidateQueries({ queryKey: ["dx-samples"] });
  const receive = useMutation({ meta: { successMessage: "Sample received" }, mutationFn: () => diagnosticsApi.receiveSample(sampleId, {}), onSuccess: afterSampleChange });
  const startProcessing = useMutation({
    meta: { successMessage: "Sample under process — HealNTrack notified" },
    mutationFn: () => diagnosticsApi.startSampleProcessing(sampleId, {}),
    onSuccess: afterSampleChange,
  });
  const reject = useMutation({
    meta: { successMessage: "Sample rejected" },
    onSuccess: afterSampleChange,
    mutationFn: () => {
      const name = rejectionReasons.data?.find(r => r.id === rejectReasonId)?.name;
      return diagnosticsApi.rejectSample(sampleId, { reason: rejectReason || name || "Rejected", rejectionReasonId: rejectReasonId || null, attachmentUrl: photoUrl || null });
    },
  });
  const recollect = useMutation({ meta: { successMessage: "Sample recollected (new barcode)" }, mutationFn: () => diagnosticsApi.recollectSample(sampleId, {}), onSuccess: afterSampleChange });

  const [rOrder, setROrder] = useState(""); const [rOrderId, setROrderId] = useState<string | null>(null);
  const [rSampleId, setRSampleId] = useState(""); const [rTest, setRTest] = useState(""); const [sex, setSex] = useState<DxSexBand>("Any");
  const [age, setAge] = useState(""); const [analyte, setAnalyte] = useState(""); const [value, setValue] = useState("");
  const enter = useMutation({
    meta: { successMessage: "Result entered" },
    mutationFn: () => diagnosticsApi.enterResult({
      diagnosticOrderId: rOrder, labTestId: rTest, sampleId: rSampleId || null,
      patientSex: sex, patientAgeYears: age === "" ? null : Number(age),
      lines: [{ analyte, numericValue: value === "" ? null : Number(value) }],
    }),
    // Entry rolls the order to PartiallyResulted and can close the sample out.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dx-samples"] });
      qc.invalidateQueries({ queryKey: ["dx-orders"] });
      qc.invalidateQueries({ queryKey: ["dx-results"] });
    },
  });

  const [resultId, setResultId] = useState(""); const [signature, setSignature] = useState("");
  const [amAnalyte, setAmAnalyte] = useState(""); const [amValue, setAmValue] = useState(""); const [amReason, setAmReason] = useState("");
  const afterSignoff = () => { qc.invalidateQueries({ queryKey: ["dx-results"] }); qc.invalidateQueries({ queryKey: ["dx-orders"] }); };
  const verify = useMutation({ meta: { successMessage: "Result verified (tier 1)" }, mutationFn: () => diagnosticsApi.verifyResult(resultId, { signature }), onSuccess: afterSignoff });
  const review = useMutation({ meta: { successMessage: "Result reviewed (tier 2)" }, mutationFn: () => diagnosticsApi.reviewResult(resultId, { signature }), onSuccess: afterSignoff });
  const approve = useMutation({ meta: { successMessage: "Result approved / released (tier 3)" }, mutationFn: () => diagnosticsApi.approveResult(resultId, { signature }), onSuccess: afterSignoff });
  const amend = useMutation({ meta: { successMessage: "Result amended" }, mutationFn: () => diagnosticsApi.amendResult(resultId, { amendments: [{ analyte: amAnalyte, newNumericValue: amValue === "" ? null : Number(amValue), reason: amReason }] }), onSuccess: afterSignoff });

  const err = (...ms: { isError: boolean; error: unknown }[]) => ms.filter(m => m.isError).map((m, i) => <Alert key={i} severity="error" sx={{ mt: 1 }}>{errorMessage(m.error)}</Alert>);
  const benchBusy = receive.isPending || startProcessing.isPending || reject.isPending || recollect.isPending;

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Diagnostics · Lab Workbench</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Collect sample</Typography>
            <Stack spacing={1.5}>
              <DiagnosticOrderIdField value={order} onChange={setOrder} required />
              <DxMasterPicker kind="specimen" value={specimen} onChange={setSpecimen} required sx={{ minWidth: 200 }} />
              <DxMasterPicker kind="container" value={container} onChange={setContainer} required sx={{ minWidth: 200 }} />
              <Button variant="contained" onClick={() => collect.mutate()} disabled={collect.isPending}>Collect</Button>
            </Stack>
            {err(collect)}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Receive / process / reject sample</Typography>
            <Stack spacing={1.5}>
              <DiagnosticOrderIdField value={benchOrder} onChange={setBenchOrder} onResolved={o => setBenchOrderId(o?.id ?? null)} />
              <SamplePicker orderId={benchOrderId} value={sampleId} onChange={setSampleId} />
              <TextField select label="Rejection reason" size="small" value={rejectReasonId} onChange={e => setRejectReasonId(e.target.value)}>
                <MenuItem value="">— select —</MenuItem>
                {rejectionReasons.data?.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
              </TextField>
              <TextField label="Remarks" size="small" value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
              <TextField label="Photo / attachment URL" size="small" value={photoUrl} onChange={e => setPhotoUrl(e.target.value)} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" onClick={() => receive.mutate()} disabled={benchBusy || !sampleId}>Receive</Button>
                <Tooltip title="Put the sample on the bench / analyzer — reports 'Pending' to HealNTrack">
                  <span>
                    <Button variant="contained" onClick={() => startProcessing.mutate()} disabled={benchBusy || !sampleId}>
                      Start processing
                    </Button>
                  </span>
                </Tooltip>
                <Button variant="outlined" color="error" onClick={() => reject.mutate()} disabled={benchBusy || !sampleId || (!rejectReasonId && !rejectReason)}>Reject</Button>
                <Button variant="outlined" color="warning" onClick={() => recollect.mutate()} disabled={benchBusy || !sampleId}>Recollect</Button>
              </Stack>
            </Stack>
            {err(receive, startProcessing, reject, recollect)}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Enter result</Typography>
            <Stack spacing={1.5}>
              <DiagnosticOrderIdField value={rOrder} onChange={setROrder} onResolved={o => setROrderId(o?.id ?? null)} required />
              <LabTestPicker value={rTest} onChange={setRTest} required />
              <SamplePicker
                orderId={rOrderId} value={rSampleId} onChange={setRSampleId}
                helperText="Links the result to the tube it was run on — closes the sample out once all its tests are resulted"
              />
              <Stack direction="row" spacing={1}>
                <TextField select label="Sex" size="small" value={sex} onChange={e => setSex(e.target.value as DxSexBand)} sx={{ width: 120 }}>
                  <MenuItem value="Any">Any</MenuItem><MenuItem value="Male">Male</MenuItem><MenuItem value="Female">Female</MenuItem>
                </TextField>
                <TextField label="Age (yrs)" size="small" type="number" value={age} onChange={e => setAge(e.target.value)} sx={{ width: 110 }} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField label="Analyte" size="small" value={analyte} onChange={e => setAnalyte(e.target.value)} sx={{ flexGrow: 1 }} />
                <TextField label="Value" size="small" type="number" value={value} onChange={e => setValue(e.target.value)} sx={{ width: 120 }} />
              </Stack>
              <Button variant="contained" onClick={() => enter.mutate()} disabled={enter.isPending}>Enter</Button>
            </Stack>
            {err(enter)}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Three-tier sign-off / amend</Typography>
            <Stack spacing={1.5}>
              <TextField label="Result Id" size="small" value={resultId} onChange={e => setResultId(e.target.value)} />
              <TextField label="Digital signature" size="small" value={signature} onChange={e => setSignature(e.target.value)} helperText="Required for every sign-off tier" />
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => verify.mutate()} disabled={verify.isPending || !signature}>1 · Verify</Button>
                <Button variant="outlined" onClick={() => review.mutate()} disabled={review.isPending || !signature}>2 · Review</Button>
                <Button variant="contained" onClick={() => approve.mutate()} disabled={approve.isPending || !signature}>3 · Approve</Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">Amend (after approval)</Typography>
              <Stack direction="row" spacing={1}>
                <TextField label="Analyte" size="small" value={amAnalyte} onChange={e => setAmAnalyte(e.target.value)} sx={{ flexGrow: 1 }} />
                <TextField label="New value" size="small" type="number" value={amValue} onChange={e => setAmValue(e.target.value)} sx={{ width: 120 }} />
              </Stack>
              <TextField label="Reason" size="small" value={amReason} onChange={e => setAmReason(e.target.value)} />
              <Button variant="outlined" onClick={() => amend.mutate()} disabled={amend.isPending || !amReason}>Amend</Button>
            </Stack>
            {err(verify, review, approve, amend)}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
